import { type ComponentProps } from 'react';

// Type-only import so the `{@link Stack}` JSDoc reference below resolves.
// eslint-disable-next-line unused-imports/no-unused-imports, @typescript-eslint/no-unused-vars
import { type Stack } from './Stack';
import { cn } from './utils/cn';

/**
 * Row-axis alignment of grid items within each cell (maps to CSS `align-items`).
 *
 * Defaults to `'stretch'` when not set (CSS default), so items fill the cell height.
 */
export type GridAlign = 'baseline' | 'center' | 'end' | 'start' | 'stretch';

/**
 * Number of equal-width columns. Use `'none'` to disable column templating
 * (grid-cols-none), or pass responsive templates via `className` (e.g.
 * `className="md:grid-cols-[200px_1fr]"`).
 */
export type GridColumns = 1 | 2 | 3 | 4 | 5 | 6 | 'none';

/**
 * Controls how auto-placed items flow into the grid (maps to `grid-auto-flow`).
 *
 * - `'row'` / `'column'`: fill by rows / columns (default row).
 * - `'dense'` / `'row-dense'` / `'column-dense'`: pack items into earlier holes.
 */
export type GridFlow =
  | 'column'
  | 'column-dense'
  | 'dense'
  | 'row'
  | 'row-dense';

/**
 * Allowed values for the `gap` prop, mapped 1:1 to Tailwind's spacing scale
 * (e.g. `gap={4}` → `gap-4` → 1rem).
 */
export type GridGap =
  | 0
  | 0.5
  | 1
  | 1.5
  | 2
  | 2.5
  | 3
  | 3.5
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 12
  | 14
  | 16;

/**
 * Column-axis distribution of grid items (maps to CSS `justify-content`).
 *
 * Defaults to CSS `normal` (behaves as `'start'` for most grids) when not set.
 */
export type GridJustify =
  | 'around'
  | 'between'
  | 'center'
  | 'end'
  | 'evenly'
  | 'start'
  | 'stretch';

/**
 * Shortcut for setting both `align-items` and `justify-items` at once
 * (maps to CSS `place-items`). Applies to every grid cell.
 *
 * Note: this is an item-level property (same layer as {@link GridAlign}),
 * not a shorthand for {@link GridJustify} which is content-level.
 */
export type GridPlace = 'center' | 'end' | 'start' | 'stretch';

/**
 * Number of equal-height rows. Use `'none'` for auto rows, or pass a custom
 * row template via `className` (e.g. `className="grid-rows-[auto_1fr_auto]"`).
 */
export type GridRows = 1 | 2 | 3 | 'none';

const ALIGN_MAP: Record<GridAlign, string> = {
  baseline: 'items-baseline',
  center: 'items-center',
  end: 'items-end',
  start: 'items-start',
  stretch: 'items-stretch',
};

const COLUMNS_MAP: Record<GridColumns, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
  none: 'grid-cols-none',
};

const FLOW_MAP: Record<GridFlow, string> = {
  column: 'grid-flow-col',
  'column-dense': 'grid-flow-col-dense',
  dense: 'grid-flow-dense',
  row: 'grid-flow-row',
  'row-dense': 'grid-flow-row-dense',
};

const GAP_MAP: Record<GridGap, string> = {
  0: 'gap-0',
  0.5: 'gap-0.5',
  1: 'gap-1',
  1.5: 'gap-1.5',
  2: 'gap-2',
  2.5: 'gap-2.5',
  3: 'gap-3',
  3.5: 'gap-3.5',
  4: 'gap-4',
  5: 'gap-5',
  6: 'gap-6',
  7: 'gap-7',
  8: 'gap-8',
  9: 'gap-9',
  10: 'gap-10',
  12: 'gap-12',
  14: 'gap-14',
  16: 'gap-16',
};

const JUSTIFY_MAP: Record<GridJustify, string> = {
  around: 'justify-around',
  between: 'justify-between',
  center: 'justify-center',
  end: 'justify-end',
  evenly: 'justify-evenly',
  start: 'justify-start',
  stretch: 'justify-stretch',
};

const PLACE_MAP: Record<GridPlace, string> = {
  center: 'place-items-center',
  end: 'place-items-end',
  start: 'place-items-start',
  stretch: 'place-items-stretch',
};

const ROWS_MAP: Record<GridRows, string> = {
  1: 'grid-rows-1',
  2: 'grid-rows-2',
  3: 'grid-rows-3',
  none: 'grid-rows-none',
};

/**
 * Props for the {@link Grid} component.
 *
 * Extends standard `<div>` props so `ref`, event handlers, `data-*`,
 * `aria-*`, `style`, etc. pass through to the underlying element.
 */
export type GridProps = ComponentProps<'div'> & {
  /** Row-axis alignment of items within their cell. */
  align?: GridAlign;
  /**
   * Number of equal-width columns, or `'none'` to disable column templating.
   * Pass custom / responsive templates via `className`
   * (e.g. `className="md:grid-cols-[200px_1fr]"`).
   */
  columns?: GridColumns;
  /** Auto-placement flow direction for implicitly positioned items. */
  flow?: GridFlow;
  /** When true, applies `h-full`. @default false */
  fullHeight?: boolean;
  /**
   * When true, applies `w-full`. Grid layouts almost always want to fill
   * parent width, especially when using `fr` columns. Set to `false` when
   * the Grid is itself a flex item that should hug its content.
   * @default true
   */
  fullWidth?: boolean;
  /** Space between cells on Tailwind's spacing scale (`gap={4}` → `gap-4`). */
  gap?: GridGap;
  /** Column-axis distribution of items. */
  justify?: GridJustify;
  /**
   * Shortcut for both `align-items` and `justify-items` (maps to CSS
   * `place-items`). Item-level, same layer as `align`, not a shorthand
   * for `justify` (which is content-level).
   */
  place?: GridPlace;
  /** Number of equal-height rows, or `'none'` for auto rows. */
  rows?: GridRows;
};

/**
 * CSS Grid container with typed layout props. Use for any grid-based layout
 * (responsive card grids, split views with `fr` columns, row/column templates).
 *
 * Defaults to `fullWidth=true` because grid templates with `fr` or
 * equal-column values need a definite container width to distribute. Fall
 * back to `className` for arbitrary column / row templates.
 *
 * See also: {@link Stack} for flex layouts; raw `<div className="...">`
 * for containers that don't need flex/grid layout.
 *
 * @example
 * // Three equal columns of cards
 * <Grid columns={3} gap={4}>
 *   <Card />
 *   <Card />
 *   <Card />
 * </Grid>
 *
 * @example
 * // Responsive: single column on small, three columns from `md` up
 * <Grid columns={1} gap={4} className="md:grid-cols-3">
 *   {items.map((i) => <Card key={i.id} />)}
 * </Grid>
 *
 * @example
 * // Custom template: fixed sidebar + flexible main area
 * <Grid gap={6} className="grid-cols-[240px_1fr]">
 *   <Sidebar />
 *   <MainContent />
 * </Grid>
 */
export function Grid({
  align,
  className,
  columns,
  flow,
  fullHeight = false,
  fullWidth = true,
  gap,
  justify,
  place,
  ref,
  rows,
  ...props
}: GridProps) {
  return (
    <div
      ref={ref}
      className={cn(
        'grid',
        columns != null && COLUMNS_MAP[columns],
        rows != null && ROWS_MAP[rows],
        gap != null && GAP_MAP[gap],
        align && ALIGN_MAP[align],
        justify && JUSTIFY_MAP[justify],
        place && PLACE_MAP[place],
        flow && FLOW_MAP[flow],
        fullWidth && 'w-full',
        fullHeight && 'h-full',
        className,
      )}
      {...props}
    />
  );
}
